import type { JSX } from 'react';
import type { UseFormRegister, FieldErrors } from 'react-hook-form';
import { TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DialogFooter } from '@/components/ui/dialog';
import { CountrySelect } from '@/components/molecules/CountrySelect';

interface NewCandidateFormData {
  name: string;
  email: string;
  country?: string;
}

interface NewCandidateTabProps {
  register: UseFormRegister<NewCandidateFormData>;
  errors: FieldErrors<NewCandidateFormData>;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  isAdding: boolean;
  country?: string;
  onCountryChange: (value: string) => void;
}

export function NewCandidateTab({
  register,
  errors,
  onSubmit,
  onCancel,
  isAdding,
  country,
  onCountryChange,
}: NewCandidateTabProps): JSX.Element {
  return (
    <TabsContent value="new" className="mt-4">
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            placeholder="Enter candidate name"
            {...register('name', { required: 'Name is required' })}
          />
          {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            placeholder="Enter candidate email"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email format' },
            })}
          />
          {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
        </div>
        <div className="space-y-2">
          <Label>Country (optional)</Label>
          <CountrySelect value={country} onValueChange={onCountryChange} />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isAdding}>
            {isAdding ? 'Creating...' : 'Create & Link'}
          </Button>
        </DialogFooter>
      </form>
    </TabsContent>
  );
}
